// ─────────────────────────────────────────────────────────────────────────────
// AuthenticatedImage.tsx — an <img> for a document behind this API's auth.
//
// A plain `<img src="...">` can't carry the Bearer token the admin API
// requires, and identity documents are deliberately never public (they live
// under a private S3 prefix — see server/src/utils/s3.ts on the app). This
// fetches the bytes with the same auth header every other request uses
// (fetchApiBlob), turns them into a same-origin object URL, and renders that.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { fetchApiBlob } from "../api/client";

interface AuthenticatedImageProps {
  /** Path relative to the API base, e.g. verificationApi.mediaPath(url). */
  src: string;
  alt: string;
  style?: React.CSSProperties;
}

export default function AuthenticatedImage({ src, alt, style }: AuthenticatedImageProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [isPdf, setIsPdf] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setObjectUrl(null);
    setFailed(false);

    fetchApiBlob(src)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setIsPdf(blob.type === "application/pdf");
        setObjectUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [src]);

  if (failed) {
    return (
      <div
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f2f2f2",
          color: "#999",
          fontSize: "11px",
          fontFamily: "var(--font-sans)",
        }}
      >
        Could not load document
      </div>
    );
  }

  if (!objectUrl) {
    return (
      <div
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f2f2f2",
          color: "#999",
          fontSize: "11px",
          fontFamily: "var(--font-sans)",
        }}
      >
        Loading…
      </div>
    );
  }

  if (isPdf) {
    // A PDF cannot be drawn in an <img>; open it in a new tab from the same in-memory URL.
    return (
      <a href={objectUrl} target="_blank" rel="noopener noreferrer"
        style={{ ...style, height: "auto", minHeight: 64, display: "flex", alignItems: "center", justifyContent: "center", background: "#f2f2f2", color: "#0a0a0a", fontSize: "12px", fontWeight: 600, fontFamily: "var(--font-sans)", textDecoration: "none" }}>
        Open PDF ↗
      </a>
    );
  }

  return <img src={objectUrl} alt={alt} style={style} />;
}
